<script setup lang="ts">
import type { Schemas } from "#shopware";
import { getTranslatedProperty } from "@shopware/helpers";

const router = useRouter();
const route = useRoute();

type ListingFilter = {
  code: string;
  label: string;
  name: string;
  options: Array<Schemas["PropertyGroupOption"]>;
  entities: Array<Schemas["ProductManufacturer"]>;
};

const props = defineProps<{
  filter: ListingFilter;
  selectedFilters: Record<string, any>;
}>();

const selectedOptionIds = computed(() => [
  ...(props.selectedFilters?.manufacturer || []),
  ...(props.selectedFilters.properties || []),
]);

function handleChange(id: string, code: string) {
  if (selectedOptionIds.value?.includes(id)) {
    router.push({
      query: {
        ...route.query,
        [code]:
          route.query?.[code]
            .split("|")
            .filter((_id: string) => _id !== id)
            .join("|") || undefined,
      },
    });
  } else {
    router.push({
      query: {
        ...route.query,
        p: 1,
        [code]: route.query?.[code] ? [route.query[code], id].join("|") : id,
      },
    });
  }
}
</script>

<template>
  <div>
    <fieldset class="grid gap-3">
      <div
        v-for="option in props.filter.options || props.filter.entities"
        :key="`${option.id}-${selectedOptionIds?.includes(option.id)}`"
        class="flex items-center"
      >
        <Checkbox
          v-model="selectedOptionIds"
          :inputId="option.id"
          :value="option.id"
          :name="getTranslatedProperty(props.filter, 'name')"
          :aria-label="`${option.name} filter`"
          @change="handleChange(option.id, props.filter.code)"
        />

        <div v-if="option.media?.url">
          <img
            loading="lazy"
            class="ml-2 h-4 w-4"
            :src="option.media.url"
            :alt="option.media.translated?.alt || ''"
            :class="{
              'border-blue border-2': selectedOptionIds?.includes(option.id),
            }"
          />
        </div>

        <div
          v-else-if="option.colorHexCode"
          class="ml-2 h-4 w-4 rounded border border-gray-600"
          :style="`background-color: ${option.colorHexCode}`"
          :class="{
            'border-blue border-2': selectedOptionIds?.includes(option.id),
          }"
        />

        <label :for="option.id" class="ml-3 text-gray-600">
          {{ getTranslatedProperty(option, "name") }}
        </label>
      </div>
    </fieldset>
  </div>
</template>
